import {
  LayoutChangeEvent,
  LayoutRectangle,
  StyleProp,
  StyleSheet,
  ViewStyle,
  ActivityIndicator,
  View,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import React, { useState, useEffect, useMemo, useLayoutEffect, useRef, useCallback } from "react";
import { useTheme } from "@/hooks/useTheme";
import { TableCell } from "@/components/Table/TableCells/Cell";
import { TableRow } from "@/components/Table/Row";
import { SummaryRow } from "@/components/Table/SummaryRow";
import { SummaryCell } from "@/components/Table/SummaryCell";
import { TableFooter } from "@/components/Table/Footer";
import { useStorage } from "@/hooks/useStorage";
import { TableHeader } from "@/components/Table/Header";
import { TableViewProps, ReadonlyEntry, ReadonlyMap } from "@/components/Table/props";
import Animated, {
  useSharedValue,
  useAnimatedRef,
  configureReanimatedLogger,
  ReanimatedLogLevel,
  useAnimatedScrollHandler,
} from "react-native-reanimated";
import useContextMenu from "@/hooks/useContextMenu";
import useDateTime from "@/hooks/useDateTime";
import HorizontalScrollbar from "@/components/HorizontalScrollbar";
import { TABLE_START_WIDTH, TABLE_START_WIDTH_SELECTABLE } from "./TableStart";
import { TABLE_END_WIDTH } from "./TableEnd";
import { LinearGradient } from "expo-linear-gradient";
import { GroupBanner } from "@/components/Table/GroupBanner";
import { GroupFooter } from "@/components/Table/GroupFooter";
import { resolveColor } from "@/components/Table/utils";
import { formatLinkedRecord } from "./types";

configureReanimatedLogger({
  level: ReanimatedLogLevel.warn,
  strict: false,
});

// Helper types and functions for readonly property
type ReadonlyConfig = {
  isReadonly: boolean;
  callback?: (key: string, value: any, row?: any) => void;
};

function normalizeReadonly(
  readonly: string[] | ReadonlyEntry[] | ReadonlyMap | undefined,
  key: string,
): ReadonlyConfig {
  if (!readonly) return { isReadonly: false };

  // Handle string[] or ReadonlyEntry[]
  if (Array.isArray(readonly)) {
    for (const entry of readonly) {
      if (typeof entry === "string") {
        if (entry === key) return { isReadonly: true };
      } else if (Array.isArray(entry)) {
        const [field, callback] = entry;
        if (field === key) {
          return { isReadonly: true, callback };
        }
      }
    }
    return { isReadonly: false };
  }

  // Handle ReadonlyMap
  const value = readonly[key];
  if (value === undefined) return { isReadonly: false };
  if (value === true) return { isReadonly: true };
  return { isReadonly: true, callback: value };
}

export const TableView = ({
  headerFunc,
  search,
  searchHidesRow = false,
  selectable = true,
  override,
  canEdit = true,
  canCreate = true,
  readonly,
  ignore,
  data,
  style,
  resultsChoices = [25, 50, 100, 250],
  footerStyle,
  headerStyle,
  rowStyle,
  resizeRealtime = false,
  onAction = () => null,
  validateCell,
  cellTextCallback = (value: any) => `${value}`,
  fieldOrder,
  dataTypes,
  rowNavIcon = "exit-to-app",
  minCellWidth = 100,
  loading = false,
  onCellBlur,
  onSelect,
  storageKey = "undefined",
  query,
  headerActions,
  summary,
  headerRightIcon,
  headerRightAction,
  onCountsChange,
  writeKeys,
  onOrderByChange,
  onFilteredDataChange,
  onHideColumn,
  resetKey,
}: TableViewProps) => {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const { formatDate, formatTimestamp } = useDateTime();
  const scrollAnchor = useSharedValue<number | null>(null);
  const [topFadeVisible, setTopFadeVisible] = useState(false);
  const [bottomFadeVisible, setBottomFadeVisible] = useState(false);
  const closeDelta = 100;
  const storage = useStorage();
  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const savedResultsPerPage = storage.getNumber("resultsPerPage") as number;
  const [selectAll, setSelectAll] = useState(false);
  const [innerLoading, setInnerLoading] = useState(loading);
  const isLoading = innerLoading || loading;
  const [resultsPerPage, setResultsPerPage] = useState<number>(
    resultsChoices.includes(savedResultsPerPage) ? savedResultsPerPage : resultsChoices[0],
  );
  const [selectedRows, setSelectedRows] = useState<Record<string, any>>({});
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const [contentSize, setContentSize] = useState<{ width: number; height: number }>({ width: 100, height: 100 });
  const [layout, setLayout] = useState<LayoutRectangle>({ width: 0, height: 0, x: 0, y: 0 });

  // Reset selection when resetKey changes (triggered by external clear/refresh)
  useEffect(() => {
    if (resetKey !== undefined) {
      setSelectedRows({});
      setLastSelectedIndex(-1);
    }
  }, [resetKey]);

  // Cell update callbacks for bulk editing
  const cellUpdateCallbacks = useRef<Map<string, (value: any) => void>>(new Map());
  // Cell reset callbacks for discard functionality
  const cellResetCallbacks = useRef<Map<string, () => void>>(new Map());

  // Get selected row IDs
  const selectedRowIds = useMemo(() => Object.keys(selectedRows), [selectedRows]);

  // Register a cell update callback (and optional reset callback)
  const registerCellUpdate = useCallback((key: string, callback: (value: any) => void, resetCallback?: () => void) => {
    cellUpdateCallbacks.current.set(key, callback);
    if (resetCallback) {
      cellResetCallbacks.current.set(key, resetCallback);
    }
  }, []);

  // Unregister a cell update callback
  const unregisterCellUpdate = useCallback((key: string) => {
    cellUpdateCallbacks.current.delete(key);
    cellResetCallbacks.current.delete(key);
  }, []);

  // Clear all cell callbacks when table unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cellUpdateCallbacks.current.clear();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cellResetCallbacks.current.clear();
    };
  }, []);

  // Trigger bulk update for all selected cells in a column
  const triggerBulkUpdate = useCallback(
    (columnKey: string, value: any, rowIds: string[], writePayload?: Record<string, any>) => {
      rowIds.forEach((rowId) => {
        const key = `${rowId}-${columnKey}`;
        const callback = cellUpdateCallbacks.current.get(key);
        if (callback) {
          callback(value);
        }

        // Get the cell's registered reset callback for discard functionality
        const resetCallback = cellResetCallbacks.current.get(key);

        // Also trigger onCellBlur for dirty map update
        if (onCellBlur) {
          onCellBlur({
            rowId,
            columnKey,
            current: value,
            original: data.find((d: any) => d.id === rowId)?.[columnKey],
            modified: true,
            reset: resetCallback || (() => {}), // Use real reset callback!
            writePayload, // Pass writePayload for proper nested object handling
          });
        }
      });
    },
    [data, onCellBlur],
  );

  const [currentPage, setCurrentPage] = useState<number>(0);
  const scrollX = useSharedValue<number>(0);
  const scrollY = useSharedValue<number>(0);

  // Helper for date formatting in search
  const handleDateTimeForSearch = (type: "date" | "date-time", input: any) => {
    if (!input) return "";
    try {
      switch (type) {
        case "date":
          return formatDate(input);
        case "date-time":
          return formatTimestamp(input, true);
        default:
          return input;
      }
    } catch {
      return input;
    }
  };

  // Wrapper that auto-formats linked_record fields so table configs don't need cellTextCallback entries
  const resolvedCellTextCallback = useMemo(() => {
    return (value: any, key: string, row: any): string => {
      const dataType = dataTypes?.[key];
      if (typeof dataType === "object" && dataType.type === "linked_record" && value && typeof value === "object") {
        return formatLinkedRecord(value, dataType);
      }
      return cellTextCallback(value, key, row);
    };
  }, [cellTextCallback, dataTypes]);

  // Stage 1: Apply filters + sorting (no search)
  const filteredData = useMemo(() => {
    let processedData = [...data];

    // Apply field filters with configurable match modes
    if (query?.filters && query.filters.length > 0) {
      // Helper to evaluate a single expression
      const evalExpr = (cellValue: any, cellValueStr: string, expr: { operator: string; value: unknown }) => {
        // Handle array field operators (is_empty/is_not_empty)
        if (expr.operator === "is_empty") {
          const arr = Array.isArray(cellValue) ? cellValue : [];
          return arr.length === 0;
        }
        if (expr.operator === "is_not_empty") {
          const arr = Array.isArray(cellValue) ? cellValue : [];
          return arr.length > 0;
        }

        // Handle in/nin operators - works for both enum (single value) and array (multiple values)
        if (expr.operator === "in" || expr.operator === "nin") {
          const filterValues = Array.isArray(expr.value) ? expr.value : [expr.value];
          const normalizedFilterValues = filterValues.map((v) => v?.toString().toLowerCase() || "").filter(Boolean);
          if (normalizedFilterValues.length === 0) return true; // Empty selection = match all

          // For array cell values, check if ANY cell value matches ANY filter value (intersection)
          if (Array.isArray(cellValue)) {
            const normalizedCellValues = cellValue.map((v) => v?.toString().toLowerCase() || "").filter(Boolean);
            const hasIntersection = normalizedCellValues.some((cv) => normalizedFilterValues.includes(cv));
            return expr.operator === "in" ? hasIntersection : !hasIntersection;
          }

          // For single cell values (enum), check if value is in filter values
          const isMatch = normalizedFilterValues.includes(cellValueStr);
          return expr.operator === "in" ? isMatch : !isMatch;
        }

        const searchValue = expr.value?.toString().toLowerCase() || "";
        if (!searchValue) return true; // Empty value = match all

        // Check if cell and filter search values are numbers
        if (Number.isFinite(Number(cellValueStr)) && Number.isFinite(Number(searchValue))) {
          const searchValueNum = Number(searchValue);
          const cellValueNum = Number(cellValueStr);

          switch (expr.operator) {
            case "gte":
              return cellValueNum >= searchValueNum;
            case "lte":
              return cellValueNum <= searchValueNum;
            case "gt":
              return cellValueNum > searchValueNum;
            case "lt":
              return cellValueNum < searchValueNum;
            default:
              // Fall through to string comparison
              break;
          }
        }

        switch (expr.operator) {
          case "like":
            return cellValueStr.includes(searchValue);
          case "eq":
            return cellValueStr === searchValue;
          case "neq":
            return cellValueStr !== searchValue;
          case "gte":
            return cellValueStr >= searchValue;
          case "lte":
            return cellValueStr <= searchValue;
          case "gt":
            return cellValueStr > searchValue;
          case "lt":
            return cellValueStr < searchValue;
          default:
            return cellValueStr.includes(searchValue);
        }
      };

      // Helper to evaluate a field filter against a row
      const evalFieldFilter = (row: any, fieldFilter: (typeof query.filters)[0]) => {
        if (!fieldFilter.expressions || fieldFilter.expressions.length === 0) {
          return true;
        }

        const rawCellValue = row[fieldFilter.name];

        // For iso_week, handle subfield extraction (week = week number, year = year)
        // Format: "2026-W05" -> year=2026, week=5
        if (fieldFilter.kind === "iso_week" && typeof rawCellValue === "string") {
          const [yearPart, weekPart] = rawCellValue.split("-W");
          const cellYear = parseInt(yearPart, 10);
          const cellWeek = parseInt(weekPart, 10);

          // Check if there's any year condition in the expressions
          const hasYearCondition = fieldFilter.expressions.some((e) => e.subfield === "year");
          const currentYear = new Date().getFullYear();

          const fieldIterator = fieldFilter.matchMode === "any" ? "some" : "every";
          return fieldFilter.expressions[fieldIterator]((expr) => {
            if (expr.subfield === "year") {
              // Year condition: compare year portion
              const exprYear = parseInt(expr.value?.toString() || "0", 10);
              return evalExpr(cellYear, cellYear.toString(), { ...expr, value: exprYear });
            } else {
              // Week condition: compare week number only
              const exprWeek = parseInt(expr.value?.toString() || "0", 10);
              // If no year condition exists, implicitly filter to current year
              if (!hasYearCondition && cellYear !== currentYear) {
                return false;
              }
              return evalExpr(cellWeek, cellWeek.toString(), { ...expr, value: exprWeek });
            }
          });
        }

        // For linked_record, handle per-expression subfield extraction
        if (fieldFilter.kind === "linked_record" && rawCellValue && typeof rawCellValue === "object") {
          const fieldIterator = fieldFilter.matchMode === "any" ? "some" : "every";
          return fieldFilter.expressions[fieldIterator]((expr) => {
            const subfield = expr.subfield || "name";
            const subValue = rawCellValue[subfield]?.toString().toLowerCase() || "";
            return evalExpr(subValue, subValue, expr);
          });
        }

        const cellValue = resolvedCellTextCallback(rawCellValue, fieldFilter.name, row);
        const cellValueStr = cellValue?.toString().toLowerCase() || "";

        // Use matchMode: "any" = OR (some), "all" = AND (every, default)
        const fieldIterator = fieldFilter.matchMode === "any" ? "some" : "every";
        return fieldFilter.expressions[fieldIterator]((expr) => evalExpr(rawCellValue, cellValueStr, expr));
      };

      // Global filterMatchMode: "any" = OR (some), "all" = AND (every, default)
      const globalIterator = query.filterMatchMode === "any" ? "some" : "every";
      processedData = processedData.filter((row) => {
        return query.filters[globalIterator]((fieldFilter) => evalFieldFilter(row, fieldFilter));
      });
    }

    // Apply sorting: groupby fields as cascading primary sort, orderby as secondary
    const groupByFields = query?.groupby?.length ? query.groupby : [];
    const hasGroupBy = groupByFields.length > 0;
    const hasOrderBy = !!query?.orderby?.name && !!query?.orderby?.direction;

    if (hasGroupBy || hasOrderBy) {
      processedData = [...processedData].sort((a, b) => {
        // Primary: cascade through groupby fields (ascending, nulls/empty last)
        for (const gf of groupByFields) {
          const aG = resolvedCellTextCallback(a[gf], gf, a)?.toString().toLowerCase() ?? "";
          const bG = resolvedCellTextCallback(b[gf], gf, b)?.toString().toLowerCase() ?? "";
          if (!aG && bG) return 1;
          if (aG && !bG) return -1;
          if (aG < bG) return -1;
          if (aG > bG) return 1;
        }

        // Secondary: orderby (existing logic)
        if (hasOrderBy) {
          const aValue = resolvedCellTextCallback(a[query!.orderby!.name], query!.orderby!.name, a);
          const bValue = resolvedCellTextCallback(b[query!.orderby!.name], query!.orderby!.name, b);

          if (aValue === null || aValue === undefined) return 1;
          if (bValue === null || bValue === undefined) return -1;

          let aStr;
          let bStr;

          if (typeof aValue === "number" && typeof bValue === "number") {
            aStr = aValue;
            bStr = bValue;
          } else {
            aStr = aValue.toString().toLowerCase();
            bStr = bValue.toString().toLowerCase();
          }

          let comparison = 0;
          if (aStr < bStr) comparison = -1;
          else if (aStr > bStr) comparison = 1;

          return query!.orderby!.direction === "descending" ? -comparison : comparison;
        }

        return 0;
      });
    }

    return processedData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, query?.filters, query?.orderby, query?.groupby, resolvedCellTextCallback]);

  // Stage 2: Apply search on top of filtered + sorted data
  const filteredAndSearchedData = useMemo(() => {
    if (!searchHidesRow || !search) return filteredData;

    const searchLower = search.toLowerCase();
    const searchableFields = data[0] ? Object.keys(data[0]).filter((key) => !ignore?.includes(key)) : [];
    return filteredData.filter((row) => {
      return searchableFields.some((key) => {
        let val;
        if (dataTypes?.[key]?.toString().includes("date")) {
          val = handleDateTimeForSearch(dataTypes[key] as "date" | "date-time", row[key]);
        } else {
          val = resolvedCellTextCallback(row[key], key, row);
        }
        return typeof val === "string" && val.toLowerCase().includes(searchLower);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, search, searchHidesRow, ignore, dataTypes, data, resolvedCellTextCallback]);

  const pageCount = Math.ceil(filteredAndSearchedData.length / resultsPerPage);

  const pages = useMemo(
    () =>
      Array.from({ length: pageCount }, (_, i) =>
        filteredAndSearchedData.slice(i * resultsPerPage, (i + 1) * resultsPerPage),
      ) ?? [],
    [filteredAndSearchedData, pageCount, resultsPerPage],
  );
  const currentRows = useMemo(() => pages[currentPage] ?? [], [currentPage, pages]);

  // Load anchor fields from storage
  const [anchorFields, setAnchorFields] = useState<string[]>(() => {
    const stored = storage.getItem(`${storageKey}-anchors`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });

  // Save anchor fields when they change
  useEffect(() => {
    storage.saveFast(`${storageKey}-anchors`, JSON.stringify(anchorFields));
  }, [anchorFields, storageKey, storage]);

  // Load column order from storage (raw, without merging - we'll merge when data is ready)
  const [columnOrder, setColumnOrder] = useState<string[] | null>(() => {
    const stored = storage.getItem(`${storageKey}-column-order`);
    if (stored) {
      try {
        return JSON.parse(stored) as string[];
      } catch {
        return null;
      }
    }
    return null;
  });

  // Save column order when it changes (only if we have a valid order)
  useEffect(() => {
    if (columnOrder && columnOrder.length > 0) {
      storage.saveFast(`${storageKey}-column-order`, JSON.stringify(columnOrder));
    }
  }, [columnOrder, storageKey, storage]);

  // Derive ordered anchor and scrollable fields from columnOrder
  // If columnOrder is null (not yet loaded/merged), fall back to default order
  const defaultFields = useMemo(
    () => (data[0] ? Object.keys(data[0]).filter((k) => !ignore?.includes(k)) : []),
    [data, ignore],
  );
  const effectiveColumnOrder = columnOrder ?? defaultFields;

  const orderedAnchorFields = useMemo(
    () => effectiveColumnOrder.filter((f) => anchorFields.includes(f)),
    [effectiveColumnOrder, anchorFields],
  );
  const orderedScrollableFields = useMemo(
    () => effectiveColumnOrder.filter((f) => !anchorFields.includes(f)),
    [effectiveColumnOrder, anchorFields],
  );

  // Move column callbacks
  const moveColumnLeft = useCallback(
    (columnKey: string) => {
      setColumnOrder((prev) => {
        const current = prev ?? defaultFields;
        const idx = current.indexOf(columnKey);
        if (idx <= 0) return current;
        const next = [...current];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        return next;
      });
    },
    [defaultFields],
  );

  const moveColumnRight = useCallback(
    (columnKey: string) => {
      setColumnOrder((prev) => {
        const current = prev ?? defaultFields;
        const idx = current.indexOf(columnKey);
        if (idx < 0 || idx >= current.length - 1) return current;
        const next = [...current];
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        return next;
      });
    },
    [defaultFields],
  );

  const ignoredKeys = [...(ignore ?? []), ...(anchorFields ?? [])];
  const inferredFields = data[0] ? Object.keys(data[0]).filter((key) => !ignoredKeys.includes(key)) : [];

  const [scrollableFields, setScrollableFields] = useState<string[]>(inferredFields);

  const columnWidths = useSharedValue<Record<string, number>>({});
  const columnPositionX = useSharedValue<Record<string, number>>({});

  // Initialize columnWidths with stored values
  useLayoutEffect(() => {
    const initialWidths: Record<string, number> = {};

    // Load stored widths for anchor fields
    orderedAnchorFields.forEach((field) => {
      initialWidths[field] = storage.getNumber(`${storageKey}-${field}`) ?? 200;
    });

    // Load stored widths for scrollable fields
    orderedScrollableFields.forEach((field) => {
      initialWidths[field] = storage.getNumber(`${storageKey}-${field}`) ?? 200;
    });

    columnWidths.value = initialWidths;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedAnchorFields, orderedScrollableFields, storageKey]);

  const [columnClipping, setColumnClipping] = useState<Record<number, Record<string, boolean>>>({});
  const [_invalidCells, setInvalidCells] = useState<Set<string>>();

  const [editingCell, _setEditingCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);

  const setSelected = (row: any, shift?: boolean, rowIndex?: number) => {
    if (shift && lastSelectedIndex >= 0 && rowIndex !== undefined) {
      // Range selection when shift is held
      const start = Math.min(lastSelectedIndex, rowIndex);
      const end = Math.max(lastSelectedIndex, rowIndex);

      setSelectedRows((prev) => {
        const newRecord = { ...prev };
        // Select all rows in the range
        for (let i = start; i <= end; i++) {
          if (currentRows[i]) {
            newRecord[currentRows[i].id] = currentRows[i];
          }
        }
        return newRecord;
      });
    } else {
      // Single selection
      if (rowIndex !== undefined) {
        setLastSelectedIndex(rowIndex);
      }

      setSelectedRows((prev) => {
        const newRecord = { ...prev };
        if (newRecord[row.id]) {
          delete newRecord[row.id];
        } else {
          newRecord[row.id] = row;
        }
        return newRecord;
      });
    }
  };

  const deselect = (row: any) => {
    setSelectedRows((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
  };

  const handleSelectAll = !isLoading
    ? () => {
        setSelectAll(!selectAll);
        setSelectedRows((prev) => {
          const next = { ...prev };
          if (!selectAll) {
            pages[currentPage].map((_, i) => {
              next[pages[currentPage][i].id] = pages[currentPage][i];
            });
            return next;
          } else {
            if (pages[currentPage]) {
              pages[currentPage].map((_, i) => {
                delete next[pages[currentPage][i].id];
              });
            }
          }
          return next;
        });
      }
    : () => {};

  // Sync column order when data shape changes (new columns added, old removed)
  useEffect(() => {
    const allFields = data[0] ? Object.keys(data[0]).filter((k) => !ignore?.includes(k)) : [];
    if (allFields.length === 0) return; // Don't sync if we don't have data yet

    setColumnOrder((prev) => {
      const current = prev ?? [];
      const validStored = current.filter((f) => allFields.includes(f));
      const newFields = allFields.filter((f) => !current.includes(f));
      if (newFields.length === 0 && validStored.length === current.length) {
        return prev; // No changes needed
      }
      return [...validStored, ...newFields];
    });
  }, [data, ignore]);

  useLayoutEffect(() => {
    setScrollableFields(() =>
      data[0] ? Object.keys(data[0]).filter((key) => ![...(ignore || []), ...(anchorFields || [])].includes(key)) : [],
    );
    setInnerLoading(false);
  }, [anchorFields, ignore, data, columnOrder]);

  useEffect(() => {
    const rowIds = currentRows.map((row) => row.id);
    setSelectAll(rowIds.length === Object.keys(selectedRows).length);
    onSelect?.(selectedRows);
    // add order by and filter check here set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, currentRows, query?.orderby]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  }, [currentPage, scrollRef]);

  // Reset to first page when search or groupby changes
  useEffect(() => {
    setCurrentPage(0);
  }, [search, query?.groupby]);

  const handleLayout = (e: LayoutChangeEvent) => {
    setLayout(e.nativeEvent.layout);
  };

  const handleContentSizeChange = (w: number, h: number) => {
    setContentSize({ width: w, height: h });
  };

  const scrollHandler = useAnimatedScrollHandler((event) => {
    const currentY = event.contentOffset.y;
    if (contextMenu.visible) {
      if (scrollAnchor.value === null) scrollAnchor.value = currentY;
      if (scrollAnchor.value === null) {
        scrollAnchor.value = currentY;
      }
      const diff = Math.abs(currentY - scrollAnchor.value);
      contextMenu.setLocation((p) => {
        return {
          x: p.x ?? 0,
          y: (p.y ?? 0) + 1,
        };
      });
      if (diff > closeDelta) {
        contextMenu.setVisible(false);
        scrollAnchor.value = null;
      }
    }
    const offsetY = event.contentOffset.y;
    const maxScrollY = event.contentSize.height - event.layoutMeasurement.height;
    scrollY.set(offsetY);
    setTopFadeVisible(offsetY > 1);
    setBottomFadeVisible(offsetY < maxScrollY - 1);
  });

  useEffect(() => {
    if (layout.height > contentSize.height) {
      setTopFadeVisible(false);
      setBottomFadeVisible(false);
    } else {
      setTopFadeVisible(scrollY.value !== 0);
      setBottomFadeVisible(scrollY.value < contentSize.height - layout.height - 2);
    }
  }, [layout.height, contentSize.height, scrollY.value]);

  const handleDateTime = (type: "date" | "date-time", input: any) => {
    if (!input) return "";

    try {
      switch (type) {
        case "date":
          return formatDate(input);
        case "date-time":
          return formatTimestamp(input, true);
        default:
          return input;
      }
    } catch {
      return input;
    }
  };

  // Search filtering is now done in filteredAndSearchedData (before pagination)
  // displayRows just returns currentRows for backwards compatibility
  const displayRows = currentRows;

  // Report counts to parent
  useEffect(() => {
    onCountsChange?.({
      total: data.length,
      filtered: filteredAndSearchedData.length,
      showing: displayRows.length,
    });
  }, [data.length, filteredAndSearchedData.length, displayRows.length, onCountsChange]);

  // Report filtered data stages to parent for CSV export
  useEffect(() => {
    onFilteredDataChange?.({ filteredData, filteredAndSearchedData, currentPageData: currentRows });
  }, [filteredData, filteredAndSearchedData, currentRows, onFilteredDataChange]);

  // Compute group metadata for current page (supports hierarchical multi-field groupby)
  const groupMeta = useMemo(() => {
    const groupFields = query?.groupby?.length ? query.groupby : [];
    if (groupFields.length === 0 || displayRows.length === 0) return null;

    const maxDepth = groupFields.length - 1;

    // Build composite key from all groupby field values
    const getCompositeKey = (row: any) =>
      groupFields.map((gf) => resolvedCellTextCallback(row[gf], gf, row)?.toString() ?? "").join("|");

    // Get individual display values for each groupby field
    const getFieldValues = (row: any) =>
      groupFields.map((gf) => resolvedCellTextCallback(row[gf], gf, row)?.toString() ?? "");

    // Get raw values for enum color lookup
    const getRawValues = (row: any) => groupFields.map((gf) => row[gf]);

    // Detect per-level continuation from previous page
    const pageStartIndex = currentPage * resultsPerPage;
    const continuedLevels: boolean[] = groupFields.map(() => false);
    if (pageStartIndex > 0) {
      const prevRow = filteredAndSearchedData[pageStartIndex - 1];
      const currRow = displayRows[0];
      for (let lvl = 0; lvl < groupFields.length; lvl++) {
        const prevVal =
          resolvedCellTextCallback(prevRow[groupFields[lvl]], groupFields[lvl], prevRow)?.toString() ?? "";
        const currVal =
          resolvedCellTextCallback(currRow[groupFields[lvl]], groupFields[lvl], currRow)?.toString() ?? "";
        if (prevVal === currVal) {
          continuedLevels[lvl] = true;
        } else {
          break; // if a parent changed, children can't be "continued"
        }
      }
    }

    // Detect if last group continues on next page
    const pageEndIndex = pageStartIndex + displayRows.length - 1;
    const doesContinue =
      pageEndIndex < filteredAndSearchedData.length - 1 &&
      getCompositeKey(filteredAndSearchedData[pageEndIndex]) ===
        getCompositeKey(filteredAndSearchedData[pageEndIndex + 1]);

    // Build group segments with hierarchical banner descriptors
    type BannerDescriptor = {
      field: string;
      value: string;
      rawValue: any;
      depth: number;
      continued?: boolean;
    };
    type GroupSegment = {
      compositeKey: string;
      startIndex: number;
      count: number;
      banners: BannerDescriptor[];
    };

    const groups: GroupSegment[] = [];
    let currentKey: string | null = null;
    let prevLevelValues: string[] = [];

    displayRows.forEach((row: any, i: number) => {
      const key = getCompositeKey(row);
      if (key !== currentKey) {
        const fieldValues = getFieldValues(row);
        const rawValues = getRawValues(row);
        const banners: BannerDescriptor[] = [];

        if (i === 0) {
          // First group on page: emit all levels with per-level continuation flags
          for (let lvl = 0; lvl < groupFields.length; lvl++) {
            banners.push({
              field: groupFields[lvl],
              value: fieldValues[lvl],
              rawValue: rawValues[lvl],
              depth: lvl,
              continued: continuedLevels[lvl],
            });
          }
        } else {
          // Find shallowest level that changed
          let deepestChange = groupFields.length;
          for (let lvl = 0; lvl < groupFields.length; lvl++) {
            if (fieldValues[lvl] !== prevLevelValues[lvl]) {
              deepestChange = lvl;
              break;
            }
          }
          // Emit banners from the changed level through all children
          for (let lvl = deepestChange; lvl < groupFields.length; lvl++) {
            banners.push({
              field: groupFields[lvl],
              value: fieldValues[lvl],
              rawValue: rawValues[lvl],
              depth: lvl,
            });
          }
        }

        groups.push({
          compositeKey: key,
          startIndex: i,
          count: 1,
          banners,
        });
        prevLevelValues = [...fieldValues];
        currentKey = key;
      } else {
        groups[groups.length - 1].count++;
      }
    });

    return { groups, doesContinue, groupFields, maxDepth };
  }, [query?.groupby, displayRows, currentPage, resultsPerPage, filteredAndSearchedData, resolvedCellTextCallback]);

  const renderRow = (row: any, rowIndex: number) => {
    const computedRowStyle: StyleProp<ViewStyle> = typeof rowStyle === "function" ? rowStyle(row) : rowStyle;
    const rowId = row.id;

    return (
      <TableRow
        key={rowId}
        uid={rowId}
        rowNavIcon={rowNavIcon}
        rowIndex={rowIndex}
        selectable={selectable}
        selected={!!selectedRows[rowId]}
        isEditing={editingCell?.rowIndex === rowIndex}
        style={[
          {
            borderColor: editingCell?.[rowIndex] ? "red" : colors.border,
          },
          computedRowStyle,
        ]}
        onSelect={(shift?: boolean) => setSelected(row, shift, rowIndex)}
        onUnmount={() => deselect(row)}
        onAction={(buttonRef) => onAction(row, buttonRef)}
        scrollX={scrollX}
        loading={isLoading}
      >
        <TableRow.Anchor
          key={"r" + orderedAnchorFields.join(",")} //
        >
          {orderedAnchorFields.map((key, colIndex) => {
            let val;
            const dataType = dataTypes?.[key];
            const isRawType =
              typeof dataType === "object" &&
              (dataType.type === "array" ||
                dataType.type === "time_object" ||
                dataType.type === "rate_object" ||
                dataType.type === "override_string" ||
                dataType.type === "linked_record");
            if (isRawType) {
              // Pass raw value for complex types that render their own components
              val = row[key];
            } else if (dataType?.toString().includes("date")) {
              val = handleDateTime(dataType as "date" | "date-time", row[key]);
            } else {
              val = cellTextCallback(row[key], key, row);
            }
            if (query?.visible?.some((item) => item.name === key && item.visible === false)) return;
            const savedWidth = storage.getNumber(`${storageKey}-${key}`);
            const readonlyConfig = normalizeReadonly(readonly, key);
            // Only compute search styling when search has a value
            const searchStyle =
              search &&
              search.length > 0 &&
              typeof val === "string" &&
              val &&
              val.toLowerCase().includes(search.toLowerCase())
                ? { borderRadius: 4, borderWidth: StyleSheet.hairlineWidth, borderColor: colors.primary }
                : undefined;
            return (
              <TableCell
                key={`${rowId}-${colIndex}-${search ?? ""}`}
                rowIndex={rowIndex}
                rowId={rowId}
                value={val}
                columnPositionX={columnPositionX.value[colIndex]}
                columnKey={key}
                columnIndex={colIndex}
                columnWidths={columnWidths}
                validate={validateCell && ((params) => validateCell[key]?.(params) ?? true)}
                inputType={dataTypes && dataTypes[key]}
                writeKey={writeKeys && writeKeys[key]}
                setColumnClipping={setColumnClipping}
                editable={canEdit && !readonlyConfig.isReadonly}
                readonlyCallback={readonlyConfig.callback}
                rowData={row}
                setInvalid={setInvalidCells}
                savedWidth={savedWidth ?? 200}
                scrollY={scrollY}
                onBlur={onCellBlur}
                style={searchStyle ? [searchStyle] : undefined}
                loading={isLoading}
                minCellWidth={minCellWidth}
                selectedRowIds={selectedRowIds}
                isSelected={!!selectedRows[row.id]}
                registerUpdate={registerCellUpdate}
                unregisterUpdate={unregisterCellUpdate}
                triggerBulkUpdate={triggerBulkUpdate}
              />
            );
          })}
        </TableRow.Anchor>
        <TableRow.Cells
          key={"r" + orderedScrollableFields.join(",")} //
        >
          {orderedScrollableFields.map((key, colIndex) => {
            let val;
            const dataType = dataTypes?.[key];
            const isRawType =
              typeof dataType === "object" &&
              (dataType.type === "array" ||
                dataType.type === "time_object" ||
                dataType.type === "rate_object" ||
                dataType.type === "override_string" ||
                dataType.type === "linked_record");
            if (isRawType) {
              // Pass raw value for complex types that render their own components
              val = row[key];
            } else if (dataType?.toString().includes("date")) {
              val = handleDateTime(dataType as "date" | "date-time", row[key]);
            } else {
              val = cellTextCallback(row[key], key, row);
            }
            if (query?.visible?.some((item) => item.name === key && item.visible === false)) return;
            const savedWidth = storage.getNumber(`${storageKey}-${key}`);
            const readonlyConfig = normalizeReadonly(readonly, key);
            // Only compute search styling when search has a value
            const searchStyle =
              search &&
              search.length > 0 &&
              typeof val === "string" &&
              val &&
              val.toLowerCase().includes(search.toLowerCase())
                ? { borderRadius: 4, borderWidth: StyleSheet.hairlineWidth, borderColor: colors.primary }
                : undefined;
            return (
              <TableCell
                key={`${rowId}-${orderedAnchorFields.length + colIndex}-${search ?? ""}`}
                rowIndex={rowIndex}
                rowId={rowId}
                value={val}
                columnPositionX={columnPositionX.value[orderedAnchorFields.length + colIndex]}
                columnKey={key}
                columnIndex={colIndex}
                columnWidths={columnWidths}
                validate={validateCell && ((params) => validateCell[key]?.(params) ?? true)}
                inputType={dataTypes && dataTypes[key]}
                writeKey={writeKeys && writeKeys[key]}
                editable={canEdit && !readonlyConfig.isReadonly}
                readonlyCallback={readonlyConfig.callback}
                rowData={row}
                setColumnClipping={setColumnClipping}
                setInvalid={setInvalidCells}
                savedWidth={savedWidth ?? 200}
                scrollY={scrollY}
                onBlur={onCellBlur}
                style={searchStyle ? [searchStyle] : undefined}
                loading={isLoading}
                minCellWidth={minCellWidth}
                selectedRowIds={selectedRowIds}
                isSelected={!!selectedRows[row.id]}
                registerUpdate={registerCellUpdate}
                unregisterUpdate={unregisterCellUpdate}
                triggerBulkUpdate={triggerBulkUpdate}
              />
            );
          })}
        </TableRow.Cells>
      </TableRow>
    );
  };

  // Build rows with group banners/footers when groupby is active
  const rows = (() => {
    if (!groupMeta) {
      return displayRows.map((row: any, rowIndex: number) => renderRow(row, rowIndex));
    }

    const { groups, doesContinue, groupFields, maxDepth } = groupMeta;
    const elements: React.ReactNode[] = [];
    let groupIdx = 0;

    displayRows.forEach((row: any, rowIndex: number) => {
      const currentGroup = groups[groupIdx];

      // Insert GroupBanner(s) at the start of each group — one per level that changed
      if (rowIndex === currentGroup.startIndex) {
        for (const banner of currentGroup.banners) {
          // Look up indicator color for enum fields
          let indicatorColor: string | undefined;
          const inputType = dataTypes?.[banner.field];
          if (typeof inputType === "object" && inputType.type === "enum") {
            const option = inputType.options?.find(
              (o: { value: string; color?: string }) => o.value === banner.rawValue,
            );
            if (option?.color) {
              indicatorColor = resolveColor(option.color, colors);
            }
          }

          // Resolve readonly callback for this groupby field
          const readonlyConfig = normalizeReadonly(readonly, banner.field);

          const isDeepest = banner.depth === maxDepth;

          elements.push(
            <GroupBanner
              key={`group-banner-${banner.depth}-${banner.field}-${banner.value}-${rowIndex}`}
              field={banner.field}
              value={banner.value || "(No value)"}
              count={isDeepest ? currentGroup.count : undefined}
              depth={banner.depth}
              continued={banner.continued}
              selectable={selectable}
              headerFunc={headerFunc}
              indicatorColor={indicatorColor}
              readonlyCallback={readonlyConfig.callback}
              rawValue={banner.rawValue}
              rowData={row}
            />,
          );
        }
      }

      elements.push(renderRow(row, rowIndex));

      // Advance to next group when current group ends
      if (rowIndex === currentGroup.startIndex + currentGroup.count - 1) {
        // Append GroupFooter if this is the last group and it continues on the next page
        if (groupIdx === groups.length - 1 && doesContinue) {
          const lastBanner = currentGroup.banners[currentGroup.banners.length - 1];
          elements.push(
            <GroupFooter
              key={`group-footer-${currentGroup.compositeKey}`}
              field={groupFields[maxDepth]}
              value={lastBanner?.value || "(No value)"}
              depth={maxDepth}
              headerFunc={headerFunc}
            />,
          );
        }
        groupIdx++;
      }
    });

    return elements;
  })();

  // Calculate summary values
  const summaryValues = useMemo(() => {
    if (!summary || summary.length === 0) return {};

    const values: Record<string, any> = {};
    for (const [field, fn] of summary) {
      try {
        values[field] = fn(filteredAndSearchedData, field);
      } catch (error) {
        console.error(`Error calculating summary for field ${field}:`, error);
        values[field] = undefined;
      }
    }
    return values;
  }, [summary, filteredAndSearchedData]);

  return (
    <GestureHandlerRootView style={[styles.container, style]}>
      {/* <Button */}
      {/*   text={invalidCells?.size.toString()} */}
      {/*   icon="emoticon-lol" */}
      {/*   onPress={() => { */}
      {/*     console.log(columnWidths.get()["first_name"]); */}
      {/*   }} */}
      {/* /> */}
      <TableHeader
        key={`header-component_${orderedAnchorFields.join()}`}
        style={[styles.row, headerStyle]}
        columnPositionX={columnPositionX}
        scrollableFields={orderedScrollableFields}
        override={override}
        selected={selectAll}
        columnWidths={columnWidths}
        columnClipping={columnClipping}
        selectable={selectable}
        onSelectAll={handleSelectAll}
        headerFunc={headerFunc}
        resizeRealtime={resizeRealtime}
        anchorFields={orderedAnchorFields}
        setAnchorFields={setAnchorFields}
        scrollX={scrollX}
        scrollYLayout={layout}
        scrollYContentSize={contentSize}
        canCreate={canCreate}
        minCellWidth={minCellWidth}
        loading={isLoading}
        storageKey={storageKey}
        query={query}
        data={filteredAndSearchedData}
        currentPageData={currentRows}
        headerActions={headerActions}
        selectedRows={selectedRows}
        selectedRowIds={selectedRowIds}
        hasSummary={summary ? true : false}
        headerRightIcon={headerRightIcon}
        headerRightAction={headerRightAction}
        onMoveLeft={moveColumnLeft}
        onMoveRight={moveColumnRight}
        orderby={query?.orderby}
        onOrderByChange={onOrderByChange}
        onHideColumn={onHideColumn}
      />
      {topFadeVisible ? (
        <LinearGradient
          colors={["rgba(0,0,0,0.45)", "transparent"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.fadeTop}
        />
      ) : null}
      <Animated.ScrollView
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        contentContainerStyle={{ flexGrow: 0, gap: 10 }}
        scrollEnabled={!isLoading}
        onScroll={scrollHandler}
        onLayout={handleLayout}
        onContentSizeChange={handleContentSizeChange}
        ref={scrollRef}
        style={[
          {
            flexGrow: 0,
            backgroundColor: "transparent",
            overflowX: "visible",
          },
        ]}
      >
        {rows}
      </Animated.ScrollView>
      {bottomFadeVisible ? (
        <LinearGradient
          colors={["rgba(0,0,0,0.45)", "transparent"]}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={[styles.fadeBottom, { bottom: 46 + 28 + (summary ? 48 : 0) }]}
        />
      ) : null}
      {isLoading && (
        <ActivityIndicator
          size={80}
          style={{
            backgroundColor: colors.backdrop,
            borderTopWidth: 1,
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            flex: 1,
          }}
        />
      )}

      {/* Summary Row */}

      {summary && summary.length > 0 && (
        <SummaryRow scrollX={scrollX} columnWidths={columnWidths} selectable={selectable}>
          <SummaryRow.Anchor>
            {orderedAnchorFields.map((key, colIndex) => {
              if (query?.visible?.some((item) => item.name === key && item.visible === false)) return null;
              const savedWidth = storage.getNumber(`${storageKey}-${key}`);
              const value = summaryValues[key];
              return (
                <SummaryCell
                  key={`summary-anchor-${key}-${colIndex}`}
                  columnKey={key}
                  value={value}
                  columnWidths={columnWidths}
                  savedWidth={savedWidth ?? 200}
                  minCellWidth={minCellWidth}
                />
              );
            })}
          </SummaryRow.Anchor>
          <SummaryRow.Cells>
            {orderedScrollableFields.map((key, colIndex) => {
              if (query?.visible?.some((item) => item.name === key && item.visible === false)) return null;
              const savedWidth = storage.getNumber(`${storageKey}-${key}`);
              const value = summaryValues[key];
              return (
                <SummaryCell
                  key={`summary-cells-${key}-${colIndex}`}
                  columnKey={key}
                  value={value}
                  columnWidths={columnWidths}
                  savedWidth={savedWidth ?? 200}
                  minCellWidth={minCellWidth}
                />
              );
            })}
          </SummaryRow.Cells>
        </SummaryRow>
      )}

      {/* Horizontal Scrollbar */}
      {useMemo(() => {
        // Helper to check if a field is visible
        const isFieldVisible = (field: string) => {
          const visibilityEntry = query?.visible?.find((item) => item.name === field);
          return !visibilityEntry || visibilityEntry.visible !== false;
        };

        // Calculate anchor columns total width - ONLY VISIBLE ANCHORS
        const anchorWidth = orderedAnchorFields.reduce((total, field) => {
          if (!isFieldVisible(field)) return total; // Skip hidden
          return total + (columnWidths.value[field] || minCellWidth);
        }, 0);

        // Calculate scrollable content total width - ONLY VISIBLE SCROLLABLE
        const scrollableContentWidth = orderedScrollableFields.reduce((total, field) => {
          if (!isFieldVisible(field)) return total; // Skip hidden
          return total + (columnWidths.value[field] || minCellWidth);
        }, 0);

        // Container width is the visible area for scrollable content
        const startWidth = selectable ? TABLE_START_WIDTH_SELECTABLE : TABLE_START_WIDTH;
        const scrollableContainerWidth = Math.max(100, layout.width - anchorWidth - startWidth - TABLE_END_WIDTH);

        return (
          <HorizontalScrollbar
            scrollX={scrollX}
            contentWidth={scrollableContentWidth + 2} // +2 for borders
            containerWidth={scrollableContainerWidth}
            leftMargin={anchorWidth + startWidth}
            rightMargin={0}
            height={16}
            thumbMinWidth={40}
            autoHide={false}
          />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        scrollX,
        orderedAnchorFields,
        orderedScrollableFields,
        columnWidths.value,
        layout.width,
        minCellWidth,
        query?.visible,
      ])}

      <TableFooter
        style={[styles.footer, footerStyle]}
        pageCount={pageCount}
        currentPage={currentPage}
        setPage={setCurrentPage}
        selectedRows={Object.keys(selectedRows).length}
        resultsPerPage={resultsPerPage}
        resultsChoices={resultsChoices}
        setResultsPerPage={setResultsPerPage}
        loading={isLoading}
        total={data.length}
        filtered={filteredAndSearchedData.length}
        showing={displayRows.length}
        isSearching={!!search}
        isFiltering={(query?.filters?.length ?? 0) > 0}
      />
    </GestureHandlerRootView>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: "flex-start",
    flexGrow: 0,
    flexShrink: 1,
    gap: 10,
    flexBasis: "auto",
    maxWidth: "100%",
    overflow: "hidden",
  },
  row: {
    minHeight: 36,
  },
  fadeTop: {
    position: "absolute",
    top: 46,
    left: 0,
    right: 0,
    height: 16,
    zIndex: 2,
    elevation: 2,
  },
  fadeBottom: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 16,
    zIndex: 2,
    elevation: 2,
  },
  footer: {
    marginTop: "auto",
  },
});
